import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { ApDocumentType, ApInvoiceItem } from "@/accounts-payable/types";
import {
  formatDollars,
  hasPricebookMatch,
  matchedPricebookItemLabel,
} from "@/accounts-payable/utils";
import { PricebookMatchButton } from "./pricebook-match";
import { SectionLabel } from "./shared";

/**
 * A document's extracted lines, each with the pricebook item it is matched to
 * and the way to change that match. Same list idiom as the action cards' line
 * items: description over its SKU, cost flush right, a divider between lines.
 */
export const LineItemsSection = ({
  items,
  businessId,
  documentId,
  documentType,
  agentRunId,
}: {
  items: ApInvoiceItem[];
  businessId: string | null;
  documentId: string;
  documentType: ApDocumentType;
  /** The document's newest agent run id. */
  agentRunId: string | null;
}) => {
  if (items.length === 0) return null;

  return (
    <Stack gap="sm" fullWidth>
      <SectionLabel>Line items ({items.length})</SectionLabel>
      <Stack gap="none" fullWidth className="divide-y divide-stroke-secondary">
        {items.map((item) => (
          <Stack
            key={item.id}
            gap="xs"
            fullWidth
            className="py-3 first:pt-0 last:pb-0"
          >
            <Stack
              direction="row"
              justify="between"
              gap="md"
              align="start"
              fullWidth
            >
              <Stack gap="none" className="min-w-0">
                <Typography variant="bodyW2">
                  {item.quantity} × {item.description}
                </Typography>
                {item.sku && (
                  <Typography variant="captionW1" textColor="tertiary">
                    {item.sku}
                  </Typography>
                )}
              </Stack>
              <Typography variant="bodyW2" className="flex-none tabular-nums">
                {formatDollars(item.unit_cost)}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              justify="between"
              gap="md"
              align="center"
              fullWidth
            >
              <PricebookMatchSummary item={item} />
              <PricebookMatchButton
                businessId={businessId}
                documentId={documentId}
                documentType={documentType}
                agentRunId={agentRunId}
                item={item}
              />
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

/** "Pricebook: <item> · $cost (manual)", or why there is no item. */
const PricebookMatchSummary = ({ item }: { item: ApInvoiceItem }) => {
  const manual = item.pricebook_match_strategy === "manual";
  if (!hasPricebookMatch(item)) {
    return (
      <Typography variant="captionW1" textColor="tertiary">
        {manual ? "Not in the pricebook (set by hand)" : "No pricebook match"}
      </Typography>
    );
  }

  return (
    <Typography variant="captionW1" textColor="secondary" className="min-w-0">
      Pricebook: {matchedPricebookItemLabel(item)}
      {item.pricebook_cost != null &&
        ` · ${formatDollars(item.pricebook_cost)}`}
      {manual && <span className="text-tertiary-text"> (set by hand)</span>}
    </Typography>
  );
};
